import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { useTasks } from "../features/tasks/useTasks";
import { approveTask, completeTask, deleteTask, rejectTask, setTaskProgress } from "../api/tasks";
import { TaskStats } from "../features/tasks/TaskStats";
import { TaskFilters } from "../features/tasks/TaskFilters";
import { TaskList } from "../features/tasks/TaskList";
import { TaskForm } from "../features/tasks/TaskForm";
import { TaskNoteDialog } from "../features/tasks/TaskNoteDialog";
import { ConfirmDialog } from "../features/penalties/ConfirmDialog";
import { isAdmin, isBranchManager } from "../utils/role";

export default function TasksPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const st = useTasks();
  const canManage = isAdmin(user) || isBranchManager(user);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState(null);
  const [del, setDel] = useState(null);

  const run = async (fn, key) => {
    try {
      await fn();
      toast.success(t(`tasks.done.${key}`));
      st.reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("tasks.actionFailed"));
    }
  };

  const submitNote = (text) => {
    const { mode, task } = note;
    if (mode === "complete") run(() => completeTask(task.id, text), "completed");
    else run(() => rejectTask(task.id, text), "rejected");
    setNote(null);
  };

  return (
    <AppShell title={t("tasks.title")} subtitle={t("tasks.subtitle")}
      actions={canManage ? (
        <NeuButton variant="primary" data-testid="task-new-btn" className="flex items-center gap-2" onClick={() => setEditing("new")}>
          <Plus className="h-4 w-4" /> {t("tasks.new")}
        </NeuButton>
      ) : null}>
      <div className="grid gap-5" data-testid="tasks-page">
        <TaskStats stats={st.stats} active={st.filters.status} ready={st.refsLoaded}
          onPick={(s) => st.setFilters({ ...st.filters, status: st.filters.status === s ? "" : s })} />
        <TaskFilters filters={st.filters} setFilters={st.setFilters} employees={st.employees} branches={st.branches} canManage={canManage} />
        {editing ? (
          <TaskForm task={editing === "new" ? null : editing} branches={st.branches} employees={st.employees}
            onSaved={st.reload} onClose={() => setEditing(null)} />
        ) : null}
        <TaskList rows={st.rows} loading={st.loading} userId={user?.id} canManage={canManage}
          onProgress={(task, val) => run(() => setTaskProgress(task.id, val), "progress")}
          onComplete={(task) => setNote({ mode: "complete", task })}
          onApprove={(task) => run(() => approveTask(task.id), "approved")}
          onReject={(task) => setNote({ mode: "reject", task })}
          onEdit={(task) => setEditing(task)} onDelete={(task) => setDel(task)} />
      </div>
      <TaskNoteDialog state={note} onClose={() => setNote(null)} onSubmit={submitNote} />
      <ConfirmDialog open={!!del} title={t("tasks.delete.title")} body={t("tasks.delete.body")}
        onCancel={() => setDel(null)} onConfirm={() => { run(() => deleteTask(del.id), "deleted"); setDel(null); }} />
    </AppShell>
  );
}
